/* =========================================================================
   ATELIER AROMA — SHOPIFY THEME SCRIPT
   Vanilla JS, no dependencies. Everything degrades gracefully: without JS,
   forms post to Shopify normally (cart, checkout, search, contact…).

     1. Helpers
     2. Motion: word split, reveal, parallax, counters, mist, tilt
     3. Sections: hero slider, carousel, testimonials, gallery, recommendations
     4. Overlays: drawers, search, quick view (focus trap + Esc)
     5. Cart: AJAX add / change, drawer refresh, fly-to-cart
     6. Product form: variant picker, quantity
     7. Header, scroll progress, back to top, preloader
     8. Theme editor support + boot
   ========================================================================= */
(function () {
  "use strict";

  /* ---------- 1. HELPERS ---------- */
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const T = window.theme || { routes: {}, settings: {}, strings: {} };
  const parseHTML = (html) => new DOMParser().parseFromString(html, "text/html");

  function toast(text) {
    const t = $("#toast");
    if (!t) return;
    t.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg><span></span>';
    t.querySelector("span").textContent = text;
    t.classList.add("show");
    clearTimeout(toast._t);
    toast._t = setTimeout(() => t.classList.remove("show"), 2800);
  }

  /* ---------- 2. MOTION ---------- */

  /** Wrap each word of a heading so it can rise in turn (keeps <em> etc.). */
  function splitWords(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = "1";
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const w = document.createElement("span"); w.className = "w";
            const inner = document.createElement("span"); inner.textContent = part; inner.style.setProperty("--i", i++);
            w.appendChild(inner); frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== "BR") walk(n);
      });
    };
    walk(el);
  }
  function splitHeadings(root) {
    if (reduceMotion || designMode) return;
    $$("[data-split], .h-section, .h-display", root).forEach((h) => {
      if (h.closest(".cart-empty, .drawer")) return;
      splitWords(h);
      if (!h.closest("[data-reveal]") && !h.closest(".hero-slide")) requestAnimationFrame(() => h.classList.add("in"));
    });
  }

  const Reveal = (() => {
    const io = "IntersectionObserver" in window
      ? new IntersectionObserver((entries) => entries.forEach((en) => {
          if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
        }), { threshold: 0.12, rootMargin: "0px 0px -6% 0px" })
      : null;
    return {
      observe(root) {
        $$("[data-reveal]:not(.in)", root).forEach((el) => (io && !reduceMotion && !designMode ? io.observe(el) : el.classList.add("in")));
      }
    };
  })();

  const Parallax = (() => {
    let els = [], ticking = false;
    function frame() {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const host = el.parentElement.getBoundingClientRect();
        if (host.bottom < -100 || host.top > vh + 100) return;
        const offset = (host.top + host.height / 2 - vh / 2) * Number(el.dataset.parallax);
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      ticking = false;
    }
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } };
    if (!reduceMotion) { addEventListener("scroll", onScroll, { passive: true }); addEventListener("resize", onScroll); }
    return {
      refresh() {
        if (reduceMotion) return;
        $$("[data-parallax-img]").forEach((frameEl) => { const img = frameEl.querySelector("img"); if (img) img.dataset.parallax = frameEl.dataset.parallaxImg; });
        els = $$("[data-parallax]");
        frame();
      }
    };
  })();

  function countUp(root) {
    if (reduceMotion || designMode || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      const el = en.target, end = Number(el.dataset.count), pre = el.dataset.prefix || "", t0 = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / 1600), eased = 1 - Math.pow(1 - k, 3);
        el.textContent = pre + Math.round(end * eased);
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }), { threshold: 0.6 });
    $$("[data-count]", root).forEach((el) => io.observe(el));
  }

  function mist(root) {
    if (reduceMotion) return;
    $$("[data-mist]", root).forEach((m) => {
      if (m.childElementCount) return;
      let out = "";
      for (let i = 0; i < 18; i++) {
        const size = 3 + Math.random() * 9, left = Math.random() * 100, dur = 9 + Math.random() * 10, delay = -Math.random() * 18, drift = Math.round(Math.random() * 80 - 40);
        out += `<span style="left:${left.toFixed(1)}%;width:${size.toFixed(1)}px;height:${size.toFixed(1)}px;animation-duration:${dur.toFixed(1)}s;animation-delay:${delay.toFixed(1)}s;--drift:${drift}px"></span>`;
      }
      m.innerHTML = out;
    });
  }

  if (T.settings.cardTilt && !reduceMotion && matchMedia("(hover: hover)").matches) {
    document.addEventListener("pointermove", (e) => {
      const media = e.target.closest && e.target.closest(".p-media");
      if (!media) return;
      const r = media.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
      media.style.setProperty("--rx", (-y * 6).toFixed(2) + "deg");
      media.style.setProperty("--ry", (x * 8).toFixed(2) + "deg");
      media.style.setProperty("--mx", ((x + 0.5) * 100).toFixed(1) + "%");
      media.style.setProperty("--my", ((y + 0.5) * 100).toFixed(1) + "%");
    });
    document.addEventListener("pointerout", (e) => {
      const media = e.target.closest && e.target.closest(".p-media");
      if (media && !media.contains(e.relatedTarget)) { media.style.setProperty("--rx", "0deg"); media.style.setProperty("--ry", "0deg"); }
    });
  }

  /* ---------- 3. SECTIONS ---------- */

  function HeroSlider(el) {
    if (!el || el._hero) return;
    const slides = $$(".hero-slide", el), dots = $$(".hero-dot", el), counter = $(".hero-current", el);
    const delay = Number(el.dataset.delay) || 7000;
    const autoplay = el.dataset.autoplay === "true" && !reduceMotion && !designMode && slides.length > 1;
    let index = 0, timer = null, paused = false;
    el.style.setProperty("--hero-delay", delay + "ms");

    function go(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        const active = n === index;
        s.classList.toggle("is-active", active);
        s.toggleAttribute("inert", !active);
        s.setAttribute("aria-hidden", String(!active));
      });
      dots.forEach((d, n) => {
        d.classList.remove("is-active", "is-done");
        void d.offsetWidth;
        d.classList.toggle("is-active", n === index);
        d.classList.toggle("is-done", n < index);
        d.setAttribute("aria-selected", String(n === index));
      });
      if (counter) counter.textContent = String(index + 1).padStart(2, "0");
      schedule();
    }
    function schedule() { clearTimeout(timer); if (autoplay && !paused) timer = setTimeout(() => go(index + 1), delay); }
    function setPaused(v) { paused = v; el.classList.toggle("paused", v); v ? clearTimeout(timer) : schedule(); }

    dots.forEach((d, n) => d.addEventListener("click", () => go(n)));
    const prev = $("[data-hero-prev]", el), next = $("[data-hero-next]", el);
    if (prev) prev.addEventListener("click", () => go(index - 1));
    if (next) next.addEventListener("click", () => go(index + 1));
    el.addEventListener("mouseenter", () => setPaused(true));
    el.addEventListener("mouseleave", () => setPaused(false));
    el.addEventListener("focusin", () => setPaused(true));
    el.addEventListener("focusout", (e) => { if (!el.contains(e.relatedTarget)) setPaused(false); });
    el.addEventListener("keydown", (e) => { if (e.key === "ArrowLeft") go(index - 1); if (e.key === "ArrowRight") go(index + 1); });
    let x0 = null;
    el.addEventListener("pointerdown", (e) => { if (e.pointerType !== "mouse") x0 = e.clientX; });
    el.addEventListener("pointerup", (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
    });
    document.addEventListener("visibilitychange", () => (document.hidden ? clearTimeout(timer) : schedule()));
    if (!autoplay) el.classList.add("paused");
    go(0);
    el._hero = { go, destroy: () => clearTimeout(timer) };
  }

  function Carousel(el) {
    if (!el || el._carousel) return;
    const track = $(".carousel-track", el), prev = $("[data-dir='-1']", el), next = $("[data-dir='1']", el), bar = $(".carousel-progress span", el);
    if (!track) return;
    function update() {
      const max = track.scrollWidth - track.clientWidth, ratio = track.clientWidth / (track.scrollWidth || 1);
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
      if (bar) { bar.style.width = Math.min(100, ratio * 100) + "%"; bar.style.left = (max ? track.scrollLeft / max : 0) * (1 - ratio) * 100 + "%"; }
    }
    const step = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion ? "auto" : "smooth" });
    if (prev) prev.addEventListener("click", () => step(-1));
    if (next) next.addEventListener("click", () => step(1));
    track.addEventListener("scroll", update, { passive: true });
    addEventListener("resize", update);
    let down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener("pointerdown", (e) => { if (e.pointerType !== "mouse" || e.target.closest("button")) return; down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft; track.style.scrollSnapType = "none"; });
    addEventListener("pointermove", (e) => { if (!down) return; const dx = e.clientX - startX; if (Math.abs(dx) > 5) moved = true; track.scrollLeft = startLeft - dx; });
    addEventListener("pointerup", () => { if (!down) return; down = false; track.style.scrollSnapType = ""; });
    track.addEventListener("click", (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    update();
    el._carousel = true;
  }

  function Testimonials(el) {
    if (!el || el._testi) return;
    const media = $$(".testi-media > *", el), quotes = $$(".testi-quote", el), counter = $(".testi-current", el);
    let i = 0, timer;
    function go(n) {
      i = (n + quotes.length) % quotes.length;
      media.forEach((m, k) => m.classList.toggle("is-active", k === i));
      quotes.forEach((q, k) => { q.classList.toggle("is-active", k === i); q.setAttribute("aria-hidden", String(k !== i)); });
      if (counter) counter.textContent = String(i + 1).padStart(2, "0");
      clearTimeout(timer);
      if (!reduceMotion && !designMode && quotes.length > 1) timer = setTimeout(() => go(i + 1), 6500);
    }
    $$("[data-testi]", el).forEach((b) => b.addEventListener("click", () => go(i + Number(b.dataset.testi))));
    el.addEventListener("mouseenter", () => clearTimeout(timer));
    el.addEventListener("mouseleave", () => go(i));
    go(0);
    el._testi = { go, destroy: () => clearTimeout(timer) };
  }

  function Gallery(el) {
    if (!el || el._gallery) return;
    el._gallery = true;
    const stage = $("[data-stage]", el), main = stage && $("img", stage);
    if (!main) return;
    $$(".thumb", el).forEach((t) => t.addEventListener("click", () => {
      $$(".thumb", el).forEach((x) => x.setAttribute("aria-pressed", String(x === t)));
      main.style.opacity = 0;
      setTimeout(() => { main.srcset = t.dataset.srcset; main.src = t.dataset.src; main.style.opacity = 1; }, 250);
    }));
    const toggle = () => stage.classList.toggle("zoom");
    stage.addEventListener("click", toggle);
    stage.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } });
    stage.addEventListener("mousemove", (e) => {
      const r = stage.getBoundingClientRect();
      main.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
    });
    stage.addEventListener("mouseleave", () => stage.classList.remove("zoom"));
  }

  async function Recommendations(el) {
    if (!el || el._loaded || !el.dataset.url) return;
    el._loaded = true;
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const fresh = parseHTML(html).querySelector("[data-recommendations]");
      if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; initScope(el); }
    } catch (e) { /* recommendations are optional */ }
  }

  /* ---------- 4. OVERLAYS ---------- */
  const Overlay = (() => {
    let current = null, lastFocus = null;
    const focusables = (el) => $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el).filter((x) => x.offsetParent !== null);
    function open(el, focusEl) {
      if (!el) return;
      if (current && current !== el) close(true);
      lastFocus = document.activeElement;
      current = el;
      el.classList.add("open");
      el.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");
      setTimeout(() => (focusEl || focusables(el)[0] || el).focus(), 60);
    }
    function close(silent) {
      if (!current) return;
      current.classList.remove("open");
      current.setAttribute("aria-hidden", "true");
      document.body.classList.remove("no-scroll");
      const toggle = $("#menuToggle"); if (toggle) toggle.setAttribute("aria-expanded", "false");
      current = null;
      if (!silent && lastFocus && document.contains(lastFocus)) lastFocus.focus();
    }
    document.addEventListener("keydown", (e) => {
      if (!current) return;
      if (e.key === "Escape") close();
      if (e.key === "Tab") {
        const f = focusables(current);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    document.addEventListener("click", (e) => { if (current && e.target.closest("[data-close]")) close(); });
    return { open, close, get current() { return current; } };
  })();

  /* Search with Shopify Predictive Search */
  const Search = (() => {
    let t, ctrl, defaultHTML = null;
    async function run(q) {
      const out = $("#searchResults");
      if (!out) return;
      if (defaultHTML === null) defaultHTML = out.innerHTML;
      if (!q.trim()) { out.innerHTML = defaultHTML; return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      out.classList.add("predictive-loading");
      try {
        const url = `${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][prefix]=last&section_id=predictive-search`;
        const html = await (await fetch(url, { signal: ctrl.signal })).text();
        const res = parseHTML(html).querySelector(".predictive-results");
        out.innerHTML = res ? res.outerHTML : `<p class="search-hint">${T.strings.noResults}</p>`;
      } catch (e) { if (e.name !== "AbortError") out.innerHTML = `<p class="search-hint">${T.strings.noResults}</p>`; }
      out.classList.remove("predictive-loading");
    }
    document.addEventListener("input", (e) => {
      if (e.target.id !== "searchInput") return;
      clearTimeout(t); t = setTimeout(() => run(e.target.value), 220);
    });
    document.addEventListener("click", (e) => {
      const s = e.target.closest("[data-suggest]");
      if (!s) return;
      const input = $("#searchInput"); input.value = s.dataset.suggest; run(input.value); input.focus();
    });
    return { run };
  })();

  async function quickView(url) {
    const panel = $("#qvPanel"), modal = $("#quickView");
    if (!panel || !modal) { location.href = url; return; }
    try {
      const html = await (await fetch(url + (url.includes("?") ? "&" : "?") + "section_id=quick-view")).text();
      const content = parseHTML(html).querySelector(".qv-content");
      if (!content) { location.href = url; return; }
      panel.innerHTML = content.innerHTML;
      initProductForms(panel);
      Overlay.open(modal);
    } catch (e) { location.href = url; }
  }

  /* ---------- 5. CART ---------- */
  const Cart = (() => {
    function render(sections) {
      const html = sections && sections["cart-drawer"];
      if (!html) return;
      const doc = parseHTML(html);
      const freshPanel = doc.querySelector(".drawer-panel"), panel = $("#cartDrawer .drawer-panel");
      if (freshPanel && panel) panel.innerHTML = freshPanel.innerHTML;
      const countEl = doc.querySelector("[data-cart-count]");
      if (countEl) setCount(Number(countEl.dataset.cartCount));
    }
    function setCount(n) {
      const c = $("#cartCount");
      if (!c) return;
      c.textContent = n;
      c.classList.toggle("has-items", n > 0);
    }
    function celebrate(delayed) {
      const c = $("#cartCount"), btn = $("#cartOpen");
      setTimeout(() => {
        if (c) { c.classList.remove("bump"); void c.offsetWidth; c.classList.add("bump"); }
        if (btn) { btn.classList.remove("wiggle"); void btn.offsetWidth; btn.classList.add("wiggle"); }
      }, delayed ? 800 : 0);
    }
    async function add(formData, sourceImg) {
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", location.pathname);
      const res = await fetch(T.routes.cartAdd + ".js", { method: "POST", headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.error);
      flyToCart(sourceImg);
      render(data.sections);
      const animated = !!sourceImg && !reduceMotion;
      celebrate(animated);
      toast(`${data.product_title || data.title || ""} — ${T.strings.added}`);
      if (Overlay.current === $("#quickView")) Overlay.close(true);
      if (T.settings.openDrawerOnAdd) setTimeout(open, animated ? 650 : 0);
      return data;
    }
    async function change(line, quantity) {
      const lineEl = $(`#cartDrawer [data-line="${line}"]`);
      if (lineEl) lineEl.classList.add("is-loading");
      const res = await fetch(T.routes.cartChange + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line, quantity, sections: "cart-drawer", sections_url: location.pathname })
      });
      const data = await res.json();
      if (!res.ok) { toast(data.description || T.strings.error); if (lineEl) lineEl.classList.remove("is-loading"); return; }
      render(data.sections);
      if (document.body.classList.contains("template-cart")) location.reload();
    }
    function open() { const d = $("#cartDrawer"); if (d) Overlay.open(d); }
    document.addEventListener("click", (e) => {
      const q = e.target.closest("#cartDrawer [data-line-qty]");
      if (q) change(Number(q.dataset.lineQty), Number(q.dataset.qty));
    });
    return { add, open };
  })();

  function flyToCart(sourceImg) {
    if (reduceMotion || !sourceImg) return;
    const target = $("#cartOpen");
    if (!target) return;
    const from = sourceImg.getBoundingClientRect(), to = target.getBoundingClientRect();
    if (!from.width) return;
    const ghost = sourceImg.cloneNode();
    ghost.removeAttribute("srcset"); ghost.removeAttribute("sizes");
    ghost.className = "fly-ghost";
    Object.assign(ghost.style, { left: from.left + "px", top: from.top + "px", width: from.width + "px", height: from.height + "px" });
    document.body.appendChild(ghost);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2), dy = to.top + to.height / 2 - (from.top + from.height / 2);
    ghost.animate([
      { transform: "translate(0,0) scale(1)", opacity: 1, borderRadius: "2px" },
      { transform: `translate(${dx * 0.6}px, ${dy * 0.6 - 60}px) scale(.45)`, opacity: 0.95, borderRadius: "40%", offset: 0.6 },
      { transform: `translate(${dx}px, ${dy}px) scale(.06)`, opacity: 0.2, borderRadius: "50%" }
    ], { duration: 900, easing: "cubic-bezier(.5,0,.2,1)" }).onfinish = () => ghost.remove();
  }

  /* Quick "Ajouter" buttons on product cards */
  document.addEventListener("click", (e) => {
    const addBtn = e.target.closest("[data-add]");
    if (addBtn) {
      e.preventDefault();
      const fd = new FormData();
      fd.append("id", addBtn.dataset.add);
      fd.append("quantity", "1");
      const img = addBtn.closest(".p-card") && addBtn.closest(".p-card").querySelector(".p-media img.main");
      addBtn.disabled = true;
      Cart.add(fd, img).catch((err) => toast(err.message)).finally(() => (addBtn.disabled = false));
      return;
    }
    const qv = e.target.closest("[data-quick]");
    if (qv) { e.preventDefault(); quickView(qv.dataset.quick); return; }
    // The tilted image sits above the card link: send image clicks to the product page
    const media = e.target.closest(".p-card .p-media");
    if (media && !e.target.closest("button")) {
      const link = media.closest(".p-card").querySelector(".p-name a");
      if (link) location.href = link.href;
    }
  });

  /* ---------- 6. PRODUCT FORM ---------- */
  function initProductForms(root) {
    $$("[data-product-wrap]", root).forEach((wrap) => {
      if (wrap._init) return;
      wrap._init = true;
      const form = $("form[data-product-form]", wrap) || $("form", wrap);
      const idInput = $("[data-variant-id]", wrap);
      const addBtn = $("[data-add-button]", wrap), addLabel = $("[data-add-label]", wrap);
      const errorEl = $("[data-form-error]", wrap);
      const variantsEl = $("script[data-variants]", wrap);
      const variants = variantsEl ? JSON.parse(variantsEl.textContent) : null;
      const scope = wrap.closest("[data-product-page], .qv-content, .qv, .modal-panel") || document;
      const addText = addLabel ? addLabel.textContent : "";

      if (variants) {
        wrap.addEventListener("change", (e) => {
          if (!e.target.matches("[data-option-index]")) return;
          const chosen = $$(".variant-picker fieldset", wrap).map((fs) => { const c = $("input:checked", fs); return c ? c.value : null; });
          const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
          if (!v) { if (addBtn) addBtn.disabled = true; return; }
          idInput.value = v.id;
          if (addBtn) addBtn.disabled = !v.available;
          if (addLabel) addLabel.textContent = v.available ? addText : (window.theme.strings.soldOut || "Épuisé");
          const priceWrap = $("[data-price-wrap] [data-price]", scope);
          if (priceWrap) priceWrap.innerHTML = v.compare ? `<span class="sale">${v.price}</span><s>${v.compare}</s>` : v.price;
          if (wrap.closest("[data-product-page]")) {
            const url = new URL(location.href); url.searchParams.set("variant", v.id); history.replaceState({}, "", url);
          }
        });
      }

      $$("[data-q]", wrap).forEach((b) => b.addEventListener("click", () => {
        const input = $("input[name='quantity']", wrap);
        if (!input) return;
        input.value = Math.max(1, Math.min(10, (Number(input.value) || 1) + Number(b.dataset.q)));
      }));

      if (form) form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ""; }
        const img = $("[data-stage] img, .qv-media img", scope);
        if (addBtn) addBtn.setAttribute("aria-busy", "true");
        Cart.add(new FormData(form), img)
          .catch((err) => { if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; } })
          .finally(() => { if (addBtn) addBtn.removeAttribute("aria-busy"); });
      });
    });
  }

  /* Collection sorting: submit on change */
  document.addEventListener("change", (e) => {
    if (e.target.matches("[data-sort]")) e.target.form.submit();
    if (e.target.matches("[data-cart-page-qty]")) e.target.form.submit();
  });

  /* ---------- 7. HEADER & GLOBAL UI ---------- */
  const Header = (() => {
    const header = $("#siteHeader");
    const wrap = header && header.closest(".shopify-section");
    let lastY = 0;
    function heroFirst() {
      const first = $("#MainContent > .shopify-section");
      return first && first.querySelector(".hero");
    }
    function update() {
      if (!header) return;
      const y = window.scrollY, hero = heroFirst();
      const overHero = hero && y < hero.offsetHeight - header.offsetHeight - 10;
      document.body.classList.toggle("over-hero", !!overHero);
      header.classList.toggle("scrolled", y > 10 && !overHero);
      if (wrap) {
        const locked = document.body.classList.contains("no-scroll");
        wrap.classList.toggle("hidden", !locked && y > 500 && y > lastY + 2);
        if (y < lastY - 2 || y < 500) wrap.classList.remove("hidden");
      }
      lastY = y;
    }
    addEventListener("scroll", update, { passive: true });
    return { update };
  })();

  function initGlobalUI() {
    const menu = $("#menuToggle");
    if (menu) menu.addEventListener("click", () => { menu.setAttribute("aria-expanded", "true"); Overlay.open($("#mobileNav")); });
    const cartBtn = $("#cartOpen");
    if (cartBtn) cartBtn.addEventListener("click", (e) => { if (document.body.classList.contains("template-cart")) return; e.preventDefault(); Cart.open(); });
    const searchBtn = $("#searchOpen");
    if (searchBtn) searchBtn.addEventListener("click", (e) => { e.preventDefault(); Overlay.open($("#searchOverlay"), $("#searchInput")); });

    // Scroll progress + back to top
    const bar = $("#scrollProgress"), btn = $("#toTop"), ring = btn && $(".ring-fill", btn), C = 2 * Math.PI * 22;
    if (ring) ring.style.strokeDasharray = C;
    let ticking = false;
    const update = () => {
      const max = document.documentElement.scrollHeight - innerHeight, k = max > 0 ? scrollY / max : 0;
      if (bar) bar.style.transform = `scaleX(${k})`;
      if (ring) ring.style.strokeDashoffset = C * (1 - k);
      if (btn) btn.classList.toggle("show", scrollY > 700);
      ticking = false;
    };
    addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    if (btn) btn.addEventListener("click", () => scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" }));
    update();

    // Opening curtain
    const pre = $("#preloader");
    if (pre) {
      if (reduceMotion || designMode) pre.remove();
      else setTimeout(() => { pre.classList.add("done"); setTimeout(() => pre.remove(), 1200); }, 1300);
    }
  }

  /* ---------- 8. INIT + THEME EDITOR ---------- */
  function initScope(root) {
    splitHeadings(root);
    mist(root);
    $$("[data-hero]", root).forEach(HeroSlider);
    $$("[data-carousel]", root).forEach(Carousel);
    $$("[data-testimonials]", root).forEach(Testimonials);
    $$("[data-gallery]", root).forEach(Gallery);
    $$("[data-recommendations]", root).forEach(Recommendations);
    initProductForms(root);
    Reveal.observe(root);
    countUp(root);
    Parallax.refresh();
    Header.update();
  }

  document.addEventListener("shopify:section:load", (e) => initScope(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    $$("[data-hero], [data-testimonials]", e.target).forEach((el) => { const c = el._hero || el._testi; if (c) c.destroy(); });
  });
  document.addEventListener("shopify:block:select", (e) => {
    const slide = e.target.closest(".hero-slide"), hero = e.target.closest("[data-hero]");
    if (slide && hero && hero._hero) hero._hero.go(Number(slide.dataset.index));
  });

  function boot() {
    initGlobalUI();
    initScope(document);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
